import {
	AppBase,
	BLEND_NONE,
	BLEND_PREMULTIPLIED,
	Color,
	StandardMaterial,
} from "playcanvas";
import { loadAsset } from "@animech/axiom-utility";
import {
	FloorStyleName,
	floorStyleAssetName,
	WallColorName,
	wallColors,
} from "./room-names";

export const wallMaterialName = "Wall_Scene-03";

export function wallColorValue(name: WallColorName): Color {
	const hex = parseInt(wallColors[name], 16);
	return new Color(
		((hex >> 16) & 0xff) / 255,
		((hex >> 8) & 0xff) / 255,
		(hex & 0xff) / 255,
	);
}

export type RoomMaterialPair = {
	/** Opaque material for the surface's core. */
	core: StandardMaterial;
	/** Clone of `core` that fades out with the mesh's vertex alpha. */
	ring: StandardMaterial;
};

export async function loadWallMaterials(
	app: AppBase,
): Promise<RoomMaterialPair> {
	return materialPair(await loadMaterialAsset(app, wallMaterialName));
}

const floorStyles = new Map<FloorStyleName, Promise<RoomMaterialPair>>();

/**
 * Loads a floor style's material and textures. Loaded once per style, so a
 * repeat pick fetches nothing and links no new shader.
 */
export function loadFloorStyle(
	app: AppBase,
	name: FloorStyleName,
): Promise<RoomMaterialPair> {
	let pair = floorStyles.get(name);
	if (!pair) {
		pair = loadMaterialAsset(app, floorStyleAssetName(name)).then(materialPair);
		pair.catch(() => floorStyles.delete(name));
		floorStyles.set(name, pair);
	}
	return pair;
}

function materialPair(material: StandardMaterial): RoomMaterialPair {
	return { core: coreMaterial(material), ring: ringMaterial(material) };
}

/**
 * Loads a material asset and every texture its data references, so the
 * returned material is never rendered with a texture still pending.
 */
export async function loadMaterialAsset(
	app: AppBase,
	name: string,
): Promise<StandardMaterial> {
	const asset = await loadAsset({ name, type: "material" }, app);
	const textureIds = Object.entries(asset.data as Record<string, unknown>)
		.filter(([key, value]) => key.endsWith("Map") && typeof value === "number")
		.map(([, value]) => value as number);
	await Promise.all(textureIds.map((id) => loadAsset(id, app)));
	return asset.resource as StandardMaterial;
}

/**
 * Makes an authored material opaque in place. The artists' materials were
 * authored for a baked fade and are inconsistent in blending, so the app
 * normalises the two properties it owns and leaves everything else alone.
 */
export function coreMaterial(material: StandardMaterial): StandardMaterial {
	if (material.blendType !== BLEND_NONE || material.opacityMap) {
		material.blendType = BLEND_NONE;
		material.opacityMap = null;
		material.update();
	}
	return material;
}

/**
 * Tints a core material and its ring clone. Returns whether anything changed.
 *
 * The neutral wall material is authored white, and PlayCanvas compiles the
 * diffuse multiply out of the shader in that case, so without `update()` a
 * tint is a silent no-op. `update()` drops the shader variants, so it only
 * runs when the colour actually differs.
 */
export function setPairDiffuse(pair: RoomMaterialPair, color: Color): boolean {
	let changed = false;
	for (const material of [pair.core, pair.ring]) {
		if (!material.diffuse.equals(color)) {
			material.diffuse.copy(color);
			material.update();
			changed = true;
		}
	}
	return changed;
}

const ringMaterials = new Map<string, StandardMaterial>();

/**
 * The fade variant of a core material: a clone whose opacity comes from the
 * mesh's vertex alpha. Made once per core material and cached by name.
 *
 * The camera clears to transparent and the canvas is composited over the page
 * as premultiplied alpha, so the blend must be premultiplied too; a normal
 * blend leaves alpha squared in the framebuffer and the band composites white.
 */
export function ringMaterial(core: StandardMaterial): StandardMaterial {
	const cached = ringMaterials.get(core.name);
	if (cached) {
		return cached;
	}
	const ring = core.clone();
	ring.name = `${core.name}-Ring`;
	ring.blendType = BLEND_PREMULTIPLIED;
	ring.opacityVertexColor = true;
	ring.opacityMap = null;
	ring.update();
	ringMaterials.set(core.name, ring);
	return ring;
}
